import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate } from 'react-router-dom';

import { CompanyEmployeeService, CompanyService } from 'us-web-services';

import { useDependencies } from '../dependencies';

// Angular source: apps/angular/src/js/ng/parent/groups/groups.js#getUserCompanies
//
// /app/sitters/companies has no :name param, so it resolves the user's own
// company (via CompanyEmployees) and redirects to the scoped company page.
// If the user has no company (or the lookup fails), route to the explore-lists
// page with the `companyError=true` query flag — same behavior as Angular's
// `companyNoAccess()`.
export const CompaniesIndex = observer((): JSX.Element | null => {
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const userId = user.id;

    const resolveCompany = async (): Promise<void> => {
      try {
        const employeeResp = await CompanyEmployeeService.findByFilter({
          params: { userId },
        });
        const employees = employeeResp.data?.data ?? [];
        const companyId = employees[0]?.companyId;
        if (!companyId) {
          if (!cancelled) setTarget(`/parent/${userId}/explore-lists?companyError=true`);
          return;
        }

        const companyResp = await CompanyService.get(companyId, { params: { userId } });
        const alias = companyResp.data?.data?.alias;
        if (!alias) {
          if (!cancelled) setTarget(`/parent/${userId}/explore-lists?companyError=true`);
          return;
        }

        if (!cancelled) setTarget(`/sitters/companies/${alias}`);
      } catch {
        if (!cancelled) setTarget(`/parent/${userId}/explore-lists?companyError=true`);
      }
    };

    void resolveCompany();
    return (): void => {
      cancelled = true;
    };
  }, [user?.id]);

  if (!user?.id) {
    return <Navigate to='/login' replace />;
  }
  if (!target) return null;
  return <Navigate to={target} replace />;
});
